import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Recycle, Search } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { BotonesDeDescarga, descargarInforme, type Formato } from '@/shared/ui/Descargas';
import { enDinero } from '@/shared/ui/graficos';
import type { Tarifario } from '@/modules/generadores/contrato';

interface Fila {
  periodo: string;
  categoriaId: number;
  categoria: string;
  literal: string;
  identificacion: string;
  generador: string;
  pesajes: number;
  kilos: number;
  /** El peso en la unidad de la tarifa: KG o T. */
  cantidad: number;
  unidad: string;
  servicio: number;
  recargo: number;
  total: number;
  facturados: number;
}

interface PorCategoria {
  categoria: string;
  literal: string;
  pesajes: number;
  generadores: number;
  kilos: number;
  /** El peso en la unidad de la tarifa: KG o T. */
  cantidad: number;
  unidad: string;
  servicio: number;
  recargo: number;
  total: number;
}

interface Informe {
  filas: Fila[];
  categorias: PorCategoria[];
  pesajes: number;
  kilos: number;
  total: number;
  generadores: number;
}

/**
 * Lo pesado y facturado de los servicios especiales: literales b, c y d del Art. 9.
 *
 * <p>Los meses son los del CICLO, que es el mes que se factura. El informe de grandes
 * generadores llega fechado el último día del mes y se registra días después, así que filtrar
 * por la fecha del pesaje dejaría fuera lo que sí pertenece a ese mes.
 */
export function PanelDeServiciosEspeciales({ desde, hasta }: { desde: string; hasta: string }) {
  const [categoria, setCategoria] = useState('');
  const [texto, setTexto] = useState('');
  const [buscado, setBuscado] = useState('');
  const [bajando, setBajando] = useState<Formato | null>(null);

  // Los meses llegan como fecha; el informe trabaja por período AAAA-MM.
  const consulta = {
    ...(desde ? { desde: desde.slice(0, 7) } : {}),
    ...(hasta ? { hasta: hasta.slice(0, 7) } : {}),
    ...(categoria ? { categoria } : {}),
    ...(buscado ? { texto: buscado } : {}),
  };

  const tarifario = useQuery({
    queryKey: ['generadores', 'tarifario'],
    queryFn: () => api.get<Tarifario>('/generadores/tarifario'),
  });

  /**
   * La descarga lleva los MISMOS filtros que la pantalla —categoría y búsqueda incluidas—, y el
   * papel los imprime. Un informe de una sola categoría impreso sin avisarlo se lee como el de
   * todas.
   */
  async function descargar(formato: Formato) {
    setBajando(formato);
    await descargarInforme(
      `/informes/servicios-especiales/descarga?${new URLSearchParams({ ...consulta, formato })}`,
      `servicios-especiales${desde ? `-${desde.slice(0, 7)}` : ''}.${formato}`,
      'Servicios especiales',
    );
    setBajando(null);
  }

  const informe = useQuery({
    queryKey: ['informe-servicios-especiales', consulta],
    queryFn: () => api.get<Informe>('/informes/servicios-especiales', { query: consulta }),
  });

  if (informe.isLoading) {
    return <Cargando titulo="Cargando" detalle="sumando los pesajes del período" />;
  }

  const datos = informe.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
        <label className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
            Categoría
          </span>
          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="h-10 min-w-64 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none focus:border-marca-400"
          >
            <option value="">Todas las categorías</option>
            {(tarifario.data?.categorias ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
            Generador
          </span>
          <span className="relative block">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setBuscado(texto.trim())}
              onBlur={() => setBuscado(texto.trim())}
              placeholder="RUC o nombre"
              className="h-10 w-64 rounded-lg border border-armazon-200 bg-white pl-9 pr-3 text-sm text-armazon-900 outline-none focus:border-marca-400"
            />
          </span>
        </label>

        {datos && datos.filas.length > 0 && (
          <span className="ml-auto">
            <BotonesDeDescarga
              bajando={bajando}
              alDescargar={(f) => void descargar(f)}
              pdf="Descargar en PDF, con los filtros que están aplicados"
              xlsx="Descargar en Excel, con los filtros que están aplicados"
            />
          </span>
        )}
      </div>

      {!datos || datos.filas.length === 0 ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          No hay pesajes en ese rango
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dato rotulo="Facturado" valor={enDinero(datos.total)} />
            <Dato rotulo="Pesajes" valor={String(datos.pesajes)} />
            <Dato rotulo="Peso total" valor={`${datos.kilos.toLocaleString('es-EC')} KG`} />
            <Dato rotulo="Generadores" valor={String(datos.generadores)} />
          </div>

          <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white">
            <header className="border-b border-armazon-100 bg-armazon-50/60 px-5 py-3">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-armazon-800">
                <Recycle className="size-4 text-armazon-500" aria-hidden />
                Por categoría
              </h3>
            </header>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-armazon-100 text-[11px] uppercase tracking-wide text-armazon-500">
                  <th className="px-5 py-2 text-left">Categoría</th>
                  <th className="px-5 py-2 text-left">Literal</th>
                  <th className="px-5 py-2 text-right">Generadores</th>
                  <th className="px-5 py-2 text-right">Pesajes</th>
                  <th className="px-5 py-2 text-right">Peso</th>
                  <th className="px-5 py-2 text-right">Recargo</th>
                  <th className="px-5 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-armazon-100">
                {datos.categorias.map((c) => (
                  <tr key={c.categoria}>
                    <td className="px-5 py-2 uppercase text-armazon-700">{c.categoria}</td>
                    <td className="px-5 py-2 uppercase text-armazon-500">{c.literal}</td>
                    <td className="importe px-5 py-2 text-right text-armazon-600">
                      {c.generadores}
                    </td>
                    <td className="importe px-5 py-2 text-right text-armazon-600">{c.pesajes}</td>
                    <td className="importe px-5 py-2 text-right text-armazon-600">
                      {enSuUnidad(c)}
                    </td>
                    <td className="importe px-5 py-2 text-right text-armazon-600">
                      {c.recargo > 0 ? enDinero(c.recargo) : '—'}
                    </td>
                    <td className="importe px-5 py-2 text-right font-semibold text-armazon-900">
                      {enDinero(c.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white">
            <header className="border-b border-armazon-100 bg-armazon-50/60 px-5 py-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-armazon-800">
                Por generador
              </h3>
            </header>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-armazon-100 text-[11px] uppercase tracking-wide text-armazon-500">
                  <th className="px-5 py-2 text-left">Período</th>
                  <th className="px-5 py-2 text-left">Generador</th>
                  <th className="px-5 py-2 text-left">Categoría</th>
                  <th className="px-5 py-2 text-right">Peso</th>
                  <th className="px-5 py-2 text-right">Recargo</th>
                  <th className="px-5 py-2 text-right">Total</th>
                  <th className="px-5 py-2 text-right">Facturado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-armazon-100">
                {datos.filas.map((f) => (
                  <tr key={`${f.periodo}-${f.identificacion}-${f.categoriaId}`}>
                    <td className="importe px-5 py-2 text-armazon-700">{f.periodo}</td>
                    <td className="px-5 py-2 text-armazon-700">
                      <span className="importe block text-[11px] text-armazon-500">
                        {f.identificacion}
                      </span>
                      <span className="block uppercase">{f.generador}</span>
                    </td>
                    <td className="px-5 py-2 uppercase text-armazon-500">{f.categoria}</td>
                    <td className="importe px-5 py-2 text-right text-armazon-600">
                      {enSuUnidad(f)}
                    </td>
                    <td className="importe px-5 py-2 text-right text-armazon-600">
                      {f.recargo > 0 ? enDinero(f.recargo) : '—'}
                    </td>
                    <td className="importe px-5 py-2 text-right font-semibold text-armazon-900">
                      {enDinero(f.total)}
                    </td>
                    <td className="px-5 py-2 text-right text-[11px] uppercase">
                      {f.facturados === f.pesajes ? (
                        <span className="text-marca-700">Sí</span>
                      ) : f.facturados === 0 ? (
                        <span className="text-amber-700">No</span>
                      ) : (
                        <span className="text-amber-700">
                          {f.facturados} de {f.pesajes}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded-2xl border border-armazon-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
        {rotulo}
      </p>
      <p className="importe mt-1 text-xl font-bold text-armazon-900">{valor}</p>
    </div>
  );
}

/** Cada peso en la unidad con que se cobra: las categorías por tonelada, en toneladas. */
function enSuUnidad({ cantidad, unidad }: { cantidad: number; unidad: string }): string {
  const decimales = unidad === 'T' ? 3 : 2;
  return `${cantidad.toLocaleString('es-EC', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })} ${unidad}`;
}
